import { type Component, useEffect, useRef } from "react";

import { setUniversalFocus } from "./accessibilityFocus";

export interface UseA11yAutoFocusOnMountOptions {
  /**
   * Delay in milliseconds before setting accessibility focus
   * @default 100
   */
  delay?: number;
  /**
   * Also request keyboard focus (physical keyboards / D-pad). Android only; uses
   * the native AccessibilityModule.requestFocus API. iOS has no equivalent API in RN.
   * @default true
   */
  setKeyboardFocus?: boolean;
}

/**
 * Hook that automatically sets accessibility focus to a component when it mounts,
 * and on Android also requests keyboard focus so tab/D-pad order matches the
 * screen reader (see WCAG 2.4.3).
 *
 * Useful for announcing dynamic content to screen readers.
 *
 * @param options - Configuration options
 * @returns A ref to attach to the component that should receive focus
 *
 * @example
 * ```tsx
 * const headingRef = useA11yAutoFocusOnMount<React.ComponentRef<typeof Heading>>();
 *
 * return <Heading ref={headingRef}>Welcome</Heading>;
 * ```
 */
export function useA11yAutoFocusOnMount<T extends Component<unknown, unknown, unknown>>(
  options: UseA11yAutoFocusOnMountOptions = {},
) {
  const { delay = 100, setKeyboardFocus = true } = options;
  const elementRef = useRef<T>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (elementRef.current) {
        setUniversalFocus(elementRef, {
          keyboardFocus: setKeyboardFocus,
        });
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [delay, setKeyboardFocus]);

  return elementRef;
}
